import { usePanelContentBg } from "@/theme/PanelContentBgContext";
import { useTheme } from "@/theme/ThemeProvider";
import { fieldFrame, mergeFieldStyle, type FieldLabelPosition, type FieldWidth } from "@/ui/components/fieldChrome";
import { Eye, EyeSlash } from "phosphor-react-native";
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Platform,
  Pressable,
  StyleProp,
  Text,
  TextInput,
  View,
  ViewStyle,
} from "react-native";

type Props = Omit<
  React.ComponentProps<typeof TextInput>,
  "placeholder" | "placeholderTextColor"
> & {
  label: string;
  /** Wenn true: roter Pflichtfeld-Stern (*) nach dem Label */
  required?: boolean;
  value: string;
  onChangeText: (t: string) => void;
  containerStyle?: StyleProp<ViewStyle>;
  /** Optional element rendered at the right inner edge (e.g. save button) */
  rightElement?: React.ReactNode;
  /** Dauer der Floating-Animation in ms (CSS: .20s => 200) – nur bei floatingLabel=true */
  animationDuration?: number;
  /** Wenn false (Standard): Label immer oben fix. Wenn true: Label schwebt bei Fokus/Inhalt (Animation). */
  floatingLabel?: boolean;
  /** Wenn true: Kein document-click-Listener (vermeidet Fokus-/Redirect-Probleme z. B. auf Login). */
  disableGlobalClickSync?: boolean;
  /** Hinweis unter dem Feld. Das native Placeholder bleibt leer. */
  hint?: string;
  /** Standard „inset“: Label im Rahmen. „above“ setzt es darüber. */
  labelPosition?: FieldLabelPosition;
  /** Kompakte Höhe und radius.control. Standard bleibt die Login-Höhe. */
  dense?: boolean;
  /** weight 120, date 150, full volle Zeile. Ohne Angabe unverändert. */
  width?: FieldWidth;
  /**
   * Deutlicherer Fokusring, nur am Gewichtsfeld der Pflege.
   * Andere Felder, auch Login, behalten den 1-px-Rahmen.
   */
  emphasisFocus?: boolean;
};

/**
 * Ohne eigenes accessibilityLabel ist der zugängliche Name das sichtbare Label.
 * Ein gesetztes accessibilityLabel bleibt unverändert.
 */
export function fieldAccessibleName(label: string, requested: unknown): string {
  if (typeof requested === "string" && requested.trim() !== "") return requested;
  return label;
}

function getNativeInputValue(inputRef: React.RefObject<TextInput | null>): string {
  if (Platform.OS !== "web") return "";
  const node = inputRef.current as unknown as HTMLElement | undefined;
  if (!node) return "";
  const el = node as HTMLInputElement;
  if (typeof el.value === "string") return el.value;
  const input = node.querySelector?.("input");
  if (input && typeof input.value === "string") return input.value;
  return "";
}

export const FloatingLabelInput = forwardRef<TextInput, Props>(function FloatingLabelInput({
  label,
  required = false,
  value,
  onChangeText,
  containerStyle,
  rightElement,
  secureTextEntry = false,
  animationDuration = 200,
  floatingLabel = false,
  disableGlobalClickSync = false,
  hint,
  labelPosition = "inset",
  dense = false,
  width,
  emphasisFocus = false,
  ...rest
}, ref) {
  const { theme } = useTheme();
  const frame = fieldFrame(theme, { labelPosition, dense, width });
  const panelContentBg = usePanelContentBg();
  const inputRef = useRef<TextInput>(null);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);

  const {
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    multiline = false,
    numberOfLines,
    editable = true,
    accessibilityLabel: requestedLabel,
    ...restInputProps
  } = rest;
  const accessibleName = fieldAccessibleName(label, requestedLabel);
  const lines = numberOfLines ?? (multiline ? 3 : 1);
  const lineHeight = 22;
  const multilineMinHeight = lines * lineHeight;
  /** Readonly + mehrzeilig: feste Höhe wie konfiguriert, nicht am Text orientieren */
  const multilineFixedHeight =
    !editable && multiline ? 16 + multilineMinHeight + frame.paddingV : undefined;

  const setRef = useCallback(
    (node: TextInput | null) => {
      (inputRef as React.MutableRefObject<TextInput | null>).current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<TextInput | null>).current = node;
    },
    [ref]
  );

  const isPassword = secureTextEntry;
  const floating = floatingLabel ? (focused || value.length > 0) : true;

  const borderColor = focused
    ? theme.color.inputFocusBorder
    : theme.color.inputBorder;
  const bgColor = panelContentBg ?? "transparent";
  const focusLabelColor = theme.color.inputFocusBorder;
  const labelColorUnfocused = theme.color.inputPlaceholder;

  const labelColor = useMemo(() => {
    if (floating && focused) return focusLabelColor;
    if (floating) return theme.color.inputLabelFloatingColor;
    return labelColorUnfocused;
  }, [floating, focused, focusLabelColor, labelColorUnfocused, theme.color.inputLabelFloatingColor]);

  const textColor = theme.color.text;

  // ---- Animated label (nur bei floatingLabel=true; sonst Label immer oben fix) ----
  const anim = useRef(new Animated.Value(floating ? 1 : 0)).current;

  useLayoutEffect(() => {
    if (!floatingLabel) return;
    Animated.timing(anim, {
      toValue: floating ? 1 : 0,
      duration: animationDuration,
      useNativeDriver: false,
    }).start();
  }, [floatingLabel, anim, floating, animationDuration]);

  // Web: Autofill erkennen – native Wert lesen und State synchronisieren, damit Label hochgeht
  const syncNativeValue = useCallback(() => {
    if (Platform.OS !== "web") return;
    try {
      const nativeVal = getNativeInputValue(inputRef);
      if (nativeVal.length > 0 && nativeVal !== value) {
        onChangeText(nativeVal);
      }
    } catch {
      // DOM-Zugriff kann in manchen Kontexten (z. B. Login-Fokus) fehlschlagen – nicht werfen
    }
  }, [value, onChangeText]);

  useEffect(() => {
    if (Platform.OS !== "web" || disableGlobalClickSync) return;
    const t1 = setTimeout(syncNativeValue, 100);
    const t2 = setTimeout(syncNativeValue, 400);
    const t3 = setTimeout(syncNativeValue, 800);
    const t4 = setTimeout(syncNativeValue, 1500);
    const t5 = setTimeout(syncNativeValue, 2500);
    const interval = setInterval(syncNativeValue, 300);
    const stop = setTimeout(() => clearInterval(interval), 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [syncNativeValue, disableGlobalClickSync]);

  // Web: Beim ersten Klick irgendwo (oder Fenster-Fokus) Autofill-Werte lesen – dann geht das Label hoch
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined" || disableGlobalClickSync) return;
    const onInteraction = () => {
      try {
        syncNativeValue();
      } catch {
        // Keine Fehler nach außen, damit Fokus/Klick nicht „rauswirft“
      }
    };
    document.addEventListener("click", onInteraction, true);
    window.addEventListener("focus", onInteraction);
    return () => {
      document.removeEventListener("click", onInteraction, true);
      window.removeEventListener("focus", onInteraction);
    };
  }, [syncNativeValue, disableGlobalClickSync]);

  const labelTop = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [14, -8],
  });

  const labelFontSize = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [14, 12],
  });

  // Optional: tiny "lift" for nicer feel
  const labelTranslateY = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -1],
  });

  // Web: Autofill-Hintergrund + kein Fokus-Rahmen (weder input noch textarea)
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const id = "ferina-autofill-override";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 30px transparent inset !important;
        box-shadow: 0 0 0 30px transparent inset !important;
      }
      input:focus, textarea:focus {
        outline: none !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Auf Native: Fokus-Schatten weglassen, damit Tastatur-Öffnen + Shadow-Layout nicht kollidieren (Fokus/Tastatur bleibt).
  const focusShadowStyle =
    Platform.OS === "web" && focused && theme.inputFocusShadow
      ? theme.inputFocusShadow
      : undefined;
  const strongFocus = emphasisFocus && focused;
  const focusOutline =
    Platform.OS === "web" && strongFocus
      ? {
          outlineWidth: 2,
          outlineStyle: "solid" as const,
          outlineColor: theme.color.inputFocusBorder,
          outlineOffset: 2,
        }
      : null;

  const field = (
    <View
      style={[
        {
          position: "relative",
          borderWidth: strongFocus ? 2 : 1,
          borderColor,
          backgroundColor: bgColor,
          borderRadius: frame.radius,
          paddingHorizontal: frame.paddingH,
          paddingTop: frame.paddingTop,
          paddingBottom: frame.paddingV,
          paddingRight: isPassword ? 44 : (rightElement ? 44 : frame.paddingH),
          ...(multilineFixedHeight != null
            ? { height: multilineFixedHeight, minHeight: multilineFixedHeight }
            : { minHeight: multiline ? 16 + multilineMinHeight + frame.paddingV : frame.minHeight }),
          flexDirection: "row",
          alignItems: multiline ? "flex-start" : "center",
        },
        focusShadowStyle,
        focusOutline,
        hint || frame.above || frame.widthStyle ? undefined : containerStyle,
      ]}
    >
      {/* Label: bei floatingLabel=false immer oben fix; bei floatingLabel=true animiert */}
      {frame.above ? null : floatingLabel ? (
        <Animated.Text
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: 12,
            top: labelTop,
            fontSize: labelFontSize,
            color: labelColor,
            transform: [{ translateY: labelTranslateY }],
            backgroundColor: floating ? (panelContentBg ?? theme.color.inputLabelBg) : "transparent",
            paddingHorizontal: floating ? 6 : 0,
            borderRadius: 6,
          }}
        >
          {label}
          {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
        </Animated.Text>
      ) : (
        <Text
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: 12,
            top: -8,
            fontSize: frame.labelSize,
            color: focused ? focusLabelColor : theme.color.inputLabelFloatingColor,
            backgroundColor: panelContentBg ?? theme.color.inputLabelBg,
            paddingHorizontal: 6,
            borderRadius: 6,
          }}
        >
          {label}
          {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
        </Text>
      )}

      <TextInput
        ref={setRef}
        value={value}
        onChangeText={onChangeText}
        editable={editable}
        onFocus={(e) => {
          setFocused(true);
          onFocusProp?.(e);
          if (Platform.OS === "web" && !disableGlobalClickSync) {
            setTimeout(syncNativeValue, 0);
            setTimeout(syncNativeValue, 100);
          }
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlurProp?.(e);
        }}
        secureTextEntry={isPassword && !visible}
        autoCapitalize="none"
        autoCorrect={false}
        placeholder=""
        multiline={multiline}
        numberOfLines={numberOfLines}
        textAlignVertical={multiline ? "top" : undefined}
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          paddingTop: 0,
          paddingRight: isPassword ? 8 : (rightElement ? 8 : 0),
          paddingBottom: 0,
          minHeight: multiline ? multilineMinHeight : 20,
          color: textColor,
          fontSize: 14,
          fontWeight: "600",
          borderWidth: 0,
          outlineWidth: 0,
        }}
        placeholderTextColor="transparent"
        {...restInputProps}
        accessibilityLabel={accessibleName}
      />

      {isPassword && (
        <Pressable
          onPress={() => setVisible((v) => !v)}
          hitSlop={10}
          style={{
            position: "absolute",
            right: 8,
            top: 16,
            marginTop: -9,
            padding: 8,
            borderRadius: 10,
          }}
          accessibilityRole="button"
          accessibilityLabel={
            visible ? "Passwort verbergen" : "Passwort anzeigen"
          }
        >
          {visible ? (
            <EyeSlash
              size={18}
              color={theme.color.inputIcon}
            />
          ) : (
            <Eye size={18} color={theme.color.inputIcon} />
          )}
        </Pressable>
      )}
      {rightElement != null && !isPassword && (
        <View
          style={{
            position: "absolute",
            right: 8,
            top: 0,
            bottom: 0,
            justifyContent: "center",
            alignItems: "center",
          }}
          pointerEvents="box-none"
        >
          {rightElement}
        </View>
      )}
    </View>
  );

  const labeled = frame.above ? (
    <View style={{ gap: theme.space.dense }}>
      <Text
        style={{
          color: theme.color.textSecondary,
          fontSize: frame.labelSize,
          fontWeight: "600",
        }}
      >
        {label}
        {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
      </Text>
      {field}
    </View>
  ) : (
    field
  );

  const framed = <View style={mergeFieldStyle(frame, hint ? undefined : containerStyle)}>{labeled}</View>;

  if (!hint) {
    return frame.widthStyle || frame.above ? framed : field;
  }

  return (
    <View style={mergeFieldStyle(frame, containerStyle)}>
      {labeled}
      <Text
        style={{
          marginTop: theme.space.xs,
          color: theme.color.textSecondary,
          fontSize: theme.fontSize.small,
        }}
      >
        {hint}
      </Text>
    </View>
  );
});
